import { AssessmentQuestion } from "shared/routes/assignments"
import { DrawingAnswer } from "shared/types/studentTypes"
import styles from "../assessment.module.scss"
import { useContext, useRef, useState } from "react"
import { AssignmentTopicContext } from "../AssignmentTopicContext"
import ToolTip from "components/General/ToolTip"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBan, faRotateLeft } from "@fortawesome/free-solid-svg-icons"
import CustomHTML from "components/CustomHTML"
import { Image } from "react-bootstrap"
import { toast } from "react-toastify"

interface Props {
    questionIndex: number
    answerIndex: number
    question: AssessmentQuestion
    answer: AssessmentQuestion["answers"][number]
    initialAnswerData: string | DrawingAnswer
    saveAssessment: () => Promise<void>
    getAnswerImagePath: (questionNumber: number, answerLetter: string) => string
}

export default function MultipleChoiceItem({ questionIndex, answerIndex, question, answer, initialAnswerData, saveAssessment, getAnswerImagePath } : Props) {
    const { refreshTopicSectionProgressCheck } = useContext(AssignmentTopicContext)

    const [eliminated, setEliminated] = useState(false)

    const wrapperRef = useRef<HTMLDivElement>(null)
    const answerItemRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    const handleAnswerSelect = (e: React.MouseEvent<HTMLInputElement>) => {
        e.preventDefault()

        if (!inputRef.current) return

        if (eliminated) {
            toast.warn("Please restore option first.")
            return
        }

        const wasChecked = inputRef.current.checked

        // clear all selections
        wrapperRef.current.parentNode
            .querySelectorAll(`.${styles.answerOption}`)
            .forEach(element => {
                element.classList.remove(styles.selectedAnswerOption)
            })

        if (wasChecked) {
            inputRef.current.checked = false
            answerItemRef.current.classList.remove(styles.selectedAnswerOption)
        } else {
            inputRef.current.checked = true
            answerItemRef.current.classList.add(styles.selectedAnswerOption)
        }

        refreshTopicSectionProgressCheck?.()
        saveAssessment?.()
    }

    const handleAnswerEliminate = (event: React.MouseEvent<SVGSVGElement>) => {
        event.stopPropagation()

        setEliminated(prev => {
            const newVal = !prev
            if (newVal) {
                answerItemRef.current.classList.add(styles.eliminated)
            } else {
                answerItemRef.current.classList.remove(styles.eliminated)
            }
            return newVal
        })

        // Clear the selected option and styling when answer is eliminated or restored
        answerItemRef.current.classList.remove(styles.selectedAnswerOption)
        inputRef.current.checked = false
    }

    return (
        <div className="d-flex justify-content-between align-items-center w-100" ref={wrapperRef}>
             <div 
                ref={answerItemRef}
                role="button" 
                key={`${answerIndex}-${answer.originalLetter === initialAnswerData}`} 
                className={`text-start me-2 ${styles.answerOption} ${answer.originalLetter === initialAnswerData && styles.selectedAnswerOption}`} 
                onClick={handleAnswerSelect}>
                <input
                    ref={inputRef}
                    type="radio"
                    id={`q${questionIndex}a${answerIndex}`}
                    name={`question-${question.number}`}
                    defaultChecked={answer.originalLetter === initialAnswerData}
                    value={answer.originalLetter}
                    data-q-no={questionIndex + 1}
                    className="d-none"
                />
                <label htmlFor={`q${questionIndex}a${answerIndex}`} role="button" className="d-inline ms-1">
                {answer.imageExists ?
                    <>
                        <span className="fw-bold">{answer.letter}. </span>
                        <Image src={`${getAnswerImagePath(question.number, answer.originalLetter)}`} alt={question.image.altText || 'Assessment Image'} fluid />
                    </>
                    :
                    <>
                        <span className="fw-bold">{answer.letter}. </span><CustomHTML html={answer.answer} />
                    </>
                }
                </label>
            </div>
            <div>
                <ToolTip title={eliminated ? "Restore Option" : "Eliminate Option"}>
                    <FontAwesomeIcon icon={eliminated ? faRotateLeft : faBan} role="button" size="xl" className={`float-end ${eliminated ? "text-primary" : "text-danger"}`} onClick={handleAnswerEliminate} />
                </ToolTip>
            </div>
        </div>
    )
}
